import { createLink } from "@tanstack/react-router";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { platformApi } from "@/app/platform";
import { Button } from "@/components/Button/Button";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { Image } from "@/components/Image/Image";
import type {
  PublicContentItemData,
  PublicContentBlockData,
} from "@/types/codegen";

import { isValidExternalUrl } from "./actionUtils";

type ContentBlockDrawerProps = {
  open: boolean;
  onClose: () => void;
  contentBlock?: PublicContentBlockData;
  onButtonClick?: (buttonText: string, actionType: string) => void;
};

export function ContentBlockDrawer({
  open,
  onClose,
  contentBlock,
  onButtonClick,
}: ContentBlockDrawerProps) {
  const intl = useIntl();

  const hasButtonContentItem = useMemo(
    () => contentBlock?.contentItems.some(hasRenderableButton) ?? false,
    [contentBlock?.contentItems],
  );

  return (
    <SwipeableDrawer
      open={open}
      onClose={onClose}
      hasFullHeight={contentBlock?.drawerStyle === "FULL"}
    >
      <div className="flex flex-col gap-2">
        {contentBlock?.contentItems.map((item, index) => (
          <ContentBlockItemRenderer
            key={`${getContentItemKey(item)}-${index}`}
            item={item}
            onButtonClick={(...args) => {
              onButtonClick?.(...args);

              onClose();
            }}
          />
        ))}

        {!hasButtonContentItem && (
          <Button variant="contained" size="large" onClick={onClose}>
            {intl.formatMessage({
              defaultMessage: "Close",
              description:
                "Generic 'Close' button label to dismiss a drawer or dialog",
            })}
          </Button>
        )}
      </div>
    </SwipeableDrawer>
  );
}

function getContentItemKey(item: PublicContentItemData) {
  const value =
    item.text ??
    item.content ??
    item.imageUrl ??
    item.imageKey ??
    item.linkUrl ??
    item.miniAppId ??
    item.items?.join("|") ??
    "empty";

  return `${item.type}-${value}`;
}

type ContentBlockItemRendererProps = {
  item: PublicContentItemData;
  onButtonClick?: (buttonText: string, actionType: string) => void;
};

function ContentBlockItemRenderer({
  item,
  onButtonClick,
}: ContentBlockItemRendererProps) {
  switch (item.type) {
    case "image":
      if (!item.imageUrl) return null;

      return (
        <Image
          src={item.imageUrl}
          alt={item.alt ?? undefined}
          className="aspect-[21/9] w-full rounded-2xl object-cover"
        />
      );

    case "heading":
      return (
        <h1 className="text-lg font-semibold text-text-title">
          {item.content}
        </h1>
      );

    case "text":
      return <p className="text-text-primary">{item.content}</p>;

    case "bullets":
      if (!item.items || item.items.length === 0) return null;

      return (
        <ul className="list-disc ps-5 text-sm text-text-primary marker:text-xs">
          {item.items.map((bulletItem, index) => (
            <li key={`${bulletItem}-${index}`}>{bulletItem}</li>
          ))}
        </ul>
      );

    case "button": {
      return <ContentBlockButton item={item} onButtonClick={onButtonClick} />;
    }

    default:
      return null;
  }
}

type ContentBlockButtonProps = {
  item: PublicContentItemData;
  onButtonClick?: (buttonText: string, actionType: string) => void;
};

const ButtonLink = createLink(Button);

function hasRenderableButton(item: PublicContentItemData) {
  if (item.type !== "button" || !item.text) {
    return false;
  }

  switch (item.action) {
    case "EXTERNAL_LINK":
      return isValidExternalUrl(item.linkUrl);
    case "MINIAPP_LINK":
      return !!item.miniAppId;
    default:
      return !!item.linkUrl && !isValidExternalUrl(item.linkUrl);
  }
}

const sharedButtonProps = {
  variant: "contained",
  size: "large",
  fullWidth: true,
} as const;

function ContentBlockButton({ item, onButtonClick }: ContentBlockButtonProps) {
  const { linkUrl, text, action, miniAppId, miniAppPath } = item;

  if (!text) {
    return null;
  }

  switch (action) {
    case "EXTERNAL_LINK":
      if (!isValidExternalUrl(linkUrl)) {
        return null;
      }

      return (
        <Button
          {...sharedButtonProps}
          onClick={() => {
            platformApi.openExternalUrl(linkUrl);
            onButtonClick?.(text, action);
          }}
        >
          {text}
        </Button>
      );

    case "MINIAPP_LINK":
      if (!miniAppId) {
        return null;
      }

      return (
        <Button
          {...sharedButtonProps}
          onClick={() => {
            platformApi.navigateToMiniApp(miniAppId, miniAppPath ?? undefined);
            onButtonClick?.(text, action);
          }}
        >
          {text}
        </Button>
      );

    default:
      if (!linkUrl || isValidExternalUrl(linkUrl)) {
        return null;
      }

      return (
        <ButtonLink
          {...sharedButtonProps}
          to={linkUrl}
          onClick={() => onButtonClick?.(text, action ?? "LINK")}
        >
          {text}
        </ButtonLink>
      );
  }
}
